import { FC, ReactNode } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { NoFlipBox } from '@utils';

import { MODAL_ICON_BADGE_SIZE, modalIconTint, type ModalIconTone } from './modalIconTint';

export interface ConfirmationButton {
  label: string | ReactNode;
  onClick: () => void | Promise<void>;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  customColor?: {
    backgroundColor?: string;
    color?: string;
    hoverBackgroundColor?: string;
  };
  /** Custom MUI sx styles - allows full styling flexibility */
  sx?: SxProps<Theme>;
  disabled?: boolean;
  isLoading?: boolean;
  startIcon?: ReactNode;
}

export interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  title: string | ReactNode;
  icon?: ReactNode;
  iconColor?: ModalIconTone;
  buttons: ConfirmationButton[];
  isLoading?: boolean;
  showCloseButton?: boolean;
  /** Content area - subcomponents can add whatever they want here */
  content?: ReactNode;
}

/**
 * Generic confirmation modal component
 * Can be used for various confirmation dialogs like delete, exit, unsaved changes, etc.
 */
export const ConfirmationModal: FC<ConfirmationModalProps> = observer(({
  open,
  onClose,
  title,
  icon,
  iconColor = 'warning',
  buttons,
  isLoading = false,
  showCloseButton: _showCloseButton = true,
  content,
}) => {
  const { dir } = useDirection();
  const theme = useTheme();

  // Shared with the remediation dialogs' shell, and no longer reading
  // `warning.lighter` / `error.lighter` / … — tokens this theme never defines, so
  // every badge was drawing with no fill. See `modalIconTint`.
  const iconColorStyles = modalIconTint(theme, iconColor);

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          // 668px, flatly. This used to also carry `maxWidth: 500`, which could
          // never apply — a min-width beats a max-width in CSS — so the dialog
          // was already this wide and the other two rules only made it look
          // negotiable. Stated once rather than as three rules that disagree.
          width: 668,
          maxWidth: '100%',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 2, position: 'relative' }}>
        <NoFlipBox
          mode={dir}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: MODAL_ICON_BADGE_SIZE,
                height: MODAL_ICON_BADGE_SIZE,
                flexShrink: 0,
                borderRadius: '50%',
                backgroundColor: iconColorStyles.bg,
                color: iconColorStyles.color,
              }}
            >
              {icon}
            </Box>
          )}
          <Typography variant="h6" component="div" fontWeight={600}>
            {title}
          </Typography>
        </NoFlipBox>
        
      </DialogTitle>

      <Divider />

      {/* Content - Subcomponents can add whatever they want here */}
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {content && <Box>{content}</Box>}
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
        }}
      >
        {buttons.map((button, index) => {
          const isFirst = index === 0;
          const isFirstOutlined = isFirst && button.variant === 'outlined';

          // Build base styles
          const baseStyles: SxProps<Theme> = {
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
          };

          // Add first outlined button styles (Cancel button default style)
          const firstOutlinedStyles: SxProps<Theme> = isFirstOutlined
            ? {
                color: 'text.primary',
                borderColor: 'grey.300',
                backgroundColor: 'background.paper',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: 'action.hover',
                },
              }
            : {};

          // Add customColor styles (backward compatibility)
          const customColorStyles: SxProps<Theme> = button.customColor
            ? {
                backgroundColor: button.customColor.backgroundColor,
                color: button.customColor.color,
                '&:hover': {
                  backgroundColor: button.customColor.hoverBackgroundColor || button.customColor.backgroundColor,
                },
              }
            : {};

          // Build sx array, filtering out undefined values
          const sxArray: Array<SxProps<Theme>> = [
            baseStyles,
            firstOutlinedStyles,
            customColorStyles,
          ];
          if (button.sx) {
            sxArray.push(button.sx);
          }

          return (
            <Button
              key={index}
              onClick={button.onClick}
              disabled={button.disabled || isLoading || button.isLoading}
              variant={button.variant || 'contained'}
              color={button.customColor || button.sx ? undefined : button.color}
              startIcon={button.isLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : button.startIcon}
              sx={sxArray as SxProps<Theme>}
            >
              {button.label}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );
});
